/**
 * Secure Storage Utility
 * Provides XSS protection for sessionStorage operations
 * 
 * SECURITY NOTE:
 * - This implementation uses sessionStorage with sanitization to prevent XSS attacks
 * - Client-side encryption is NOT implemented as it provides no real security benefit
 *   (the encryption key would be visible in the JS bundle)
 * - For production environments requiring maximum security, consider:
 *   1. Implementing httpOnly cookies for auth tokens (requires backend changes)
 *   2. Using a secure session management library
 * 
 * CURRENT SECURITY MEASURES:
 * - Input sanitization to prevent script injection
 * - JWT format validation
 * - Email format validation
 * - Automatic token invalidation on tampering detection
 */

// Sanitize input to prevent XSS attacks
const sanitizeValue = (value) => {
  if (typeof value !== 'string') {
    return null;
  }
  
  // Remove potentially dangerous characters and scripts
  const cleaned = value
    .replace(/<script[^>]*>.*?<\/script>/gi, '')
    .replace(/<iframe[^>]*>.*?<\/iframe>/gi, '')
    .replace(/javascript:/gi, '')
    .replace(/on\w+\s*=/gi, '')
    .trim();
  
  return cleaned;
};

// Validate token format (JWT structure: header.payload.signature)
const isValidJWTFormat = (token) => {
  if (!token || typeof token !== 'string') return false;
  
  const parts = token.split('.');
  if (parts.length !== 3) return false;
  
  // Basic check: each part should be base64-like
  const base64Regex = /^[A-Za-z0-9_-]+$/;
  return parts.every(part => base64Regex.test(part));
};

// Validate email format
const isValidEmail = (email) => {
  if (!email || typeof email !== 'string') return false;
  
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

export const secureStorage = {
  // Set item with sanitization
  setItem: (key, value) => {
    try {
      const sanitized = sanitizeValue(value);
      if (sanitized === null) {
        console.error(`Invalid value for key: ${key}`);
        return false;
      }
      
      // Additional validation for sensitive keys
      if (key === 'admin_token' && !isValidJWTFormat(sanitized)) {
        console.error('Invalid token format');
        return false;
      }
      
      if (key === 'admin_email' && !isValidEmail(sanitized)) {
        console.error('Invalid email format');
        return false;
      }
      
      sessionStorage.setItem(key, sanitized);
      return true;
    } catch (error) {
      console.error('Error setting secure storage:', error);
      return false;
    }
  },
  
  // Get item with validation
  getItem: (key) => {
    try {
      const value = sessionStorage.getItem(key);
      if (!value) return null;
      
      const sanitized = sanitizeValue(value);
      
      // Validate format for sensitive keys
      if (key === 'admin_token' && !isValidJWTFormat(sanitized)) {
        console.warn('Potentially tampered token detected');
        secureStorage.removeItem(key);
        return null;
      }
      
      if (key === 'admin_email' && !isValidEmail(sanitized)) {
        console.warn('Invalid email format detected');
        secureStorage.removeItem(key);
        return null;
      }
      
      return sanitized;
    } catch (error) {
      console.error('Error getting secure storage:', error);
      return null;
    }
  },
  
  // Remove item
  removeItem: (key) => {
    try {
      sessionStorage.removeItem(key);
      return true;
    } catch (error) {
      console.error('Error removing secure storage:', error);
      return false;
    }
  },
  
  // Clear all
  clear: () => {
    try {
      sessionStorage.clear();
      return true;
    } catch (error) {
      console.error('Error clearing secure storage:', error);
      return false;
    }
  }
};
